import React, { createContext, useState, useCallback, useEffect, useMemo } from 'react';
import i18next from 'i18next';
import { z } from 'zod';

import { useApi, useRequiredContext } from '../hooks';
import defaultClientLanguage from '../locales/en.json';
import { useAppInit } from './appInitContext';
import { createTranslationKeyQuery, formatTranslationKeyResponse } from './zod';

const resources = {
  locale: {
    common: defaultClientLanguage,
  },
} as const;

i18next.init({
  lng: 'locale',
  defaultNS: 'common',
  debug: true,
  fallbackLng: 'locale',
  resources,
  parseMissingKeyHandler: (key) => `Translation for key ${key} is missing`,
});

const translate = (key: string, args?: Record<string, unknown>): string =>
  i18next.t(key, args);

const zTranslationContext = z.object({
  translate: z
    .function()
    .args(z.string(), z.record(z.string(), z.unknown()).nullish())
    .returns(z.string()),
  getAppTranslation: z.function().args(z.string().array()).returns(z.void()),
});

type tTranslationContext = z.infer<typeof zTranslationContext>;

export const TranslationContext = createContext<tTranslationContext>(undefined);

export const TranslationProvider = ({ children }) => {
  const [translationsLoaded, setTranslationsLoaded] = useState<boolean>(false);
  const { params } = useAppInit();
  const { languageCode } = params;
  const api = useApi();

  const getAppTranslation = useCallback(
    async (languageKeys: string[]) => {
      const response = await api(
        `translations?iso6391=${languageCode}&${createTranslationKeyQuery(
          languageKeys,
        )}`,
        'GET',
        '*',
      );
      setTranslationsLoaded(true);
      const fetchedLanguageKeys = formatTranslationKeyResponse(response.data);

      i18next.addResourceBundle(
        'locale',
        'common',
        fetchedLanguageKeys,
        true,
        true,
      );
    },
    [api, languageCode],
  );

  useEffect(() => {
    getAppTranslation(Object.keys(defaultClientLanguage));
  }, [getAppTranslation]);

  TranslationContext.displayName = 'TranslationsProvider';

  const value = useMemo(
    () => ({
      translate,
      getAppTranslation,
    }),
    [getAppTranslation],
  );

  return (
    <TranslationContext.Provider value={value}>
      {translationsLoaded && children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => useRequiredContext(TranslationContext);
